import {useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {toPng} from 'html-to-image'
import {CommentGlyph} from '@/components/ui/icons'
import {FeedbackDialog} from '@/components/feedback/feedback-dialog'
import type {Captura} from '@/hooks/producto/feedback/use-feedback'
import {anotarRuta, patronDeRuta} from '@/lib/bitacora-sesion'

const CABECERA_PNG = 'data:image/png;base64,'

function blobDesdeDataUrl(dataUrl: string): Blob {
  const bytes = atob(dataUrl.slice(CABECERA_PNG.length))
  const buffer = new Uint8Array(bytes.length)
  for (let i = 0; i < bytes.length; i += 1) buffer[i] = bytes.charCodeAt(i)
  return new Blob([buffer], {type: 'image/png'})
}

/** Se toma tras abrir: el diálogo vive en un portal fuera de `#root` y no sale en ella. Viaja
 * como `data:` URL porque la CSP (`infra/firebase/firebase.json`) no admite `blob:` en img-src. */
async function capturarPantalla(): Promise<Captura | null> {
  try {
    const raiz = document.getElementById('root')
    if (!raiz) return null
    const url = await toPng(raiz, {pixelRatio: 1})
    if (!url.startsWith(CABECERA_PNG) || url.length === CABECERA_PNG.length) return null
    return {blob: blobDesdeDataUrl(url), url}
  } catch {
    return null
  }
}

/** Visible también en modo dios de solo lectura: un reporte no muta el tenant. Anota los cambios
 * de ruta en la bitácora porque vive montado en toda pantalla. */
export function FeedbackBoton({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {pathname} = useLocation()
  const [open, setOpen] = useState(false)
  const [captura, setCaptura] = useState<Captura | null>(null)

  useEffect(() => {
    anotarRuta(pathname)
  }, [pathname])

  const abrir = () => {
    setOpen(true)
    void capturarPantalla().then(setCaptura)
  }

  const cambiarAbierto = (siguiente: boolean) => {
    setOpen(siguiente)
    if (!siguiente) setCaptura(null)
  }

  return (
    <>
      <button
        type="button"
        data-testid="feedback-boton"
        aria-label={t('feedback.abrir')}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={abrir}
        className="topbar-link flex min-h-11 w-9 cursor-pointer items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:min-h-0 md:h-9">
        <CommentGlyph />
      </button>
      {open && (
        <FeedbackDialog
          open
          onOpenChange={cambiarAbierto}
          businessId={businessId}
          ruta={patronDeRuta(pathname)}
          captura={captura}
        />
      )}
    </>
  )
}
